import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2,
  CircleAlert,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  MapPin,
  ShieldCheck,
  User,
  Users,
} from "lucide-react";

import { API, formatApiErrorDetail } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";


/* ================================================================
   HELPERS
================================================================ */

function normalizeRole(role) {
  const value = String(role || "")
    .trim()
    .toLowerCase();

  const labels = {
    admin: "Administrador",
    supervisor: "Supervisor",
    tecnico: "Técnico",
    cliente: "Cliente",
    gestor: "Gestor",
  };

  return labels[value] || role || "Usuário";
}


function passwordStrength(password) {
  if (!password) {
    return {
      score: 0,
      label: "",
    };
  }

  let score = 0;

  if (password.length >= 6) score += 1;
  if (password.length >= 10) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  const labels = {
    1: "Muito fraca",
    2: "Fraca",
    3: "Moderada",
    4: "Forte",
    5: "Muito forte",
  };

  return {
    score,
    label: labels[score] || "",
  };
}


/* ================================================================
   COMPONENT
================================================================ */

export default function FirstAccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { branding } = useBranding();

  const token = searchParams.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [valid, setValid] = useState(false);
  const [request, setRequest] = useState(null);

  const [areas, setAreas] = useState([]);
  const [areasLoading, setAreasLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");

  const [isAreaResponsible, setIsAreaResponsible] =
    useState(false);

  const [selectedAreaIds, setSelectedAreaIds] =
    useState([]);

  const [showPassword, setShowPassword] =
    useState(false);

  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);


  /* ==============================================================
     TOKEN
  ============================================================== */

  useEffect(() => {
    let cancelled = false;

    async function validate() {
      setLoading(true);
      setError("");

      if (!token) {
        setValid(false);
        setError(
          "O link de primeiro acesso está incompleto ou inválido.",
        );
        setLoading(false);
        return;
      }

      try {
        const { data } = await axios.get(
          `${API}/auth/registration/validate`,
          {
            params: {
              token,
            },
          },
        );

        if (cancelled) return;

        setRequest(data?.request || null);
        setValid(Boolean(data?.valid));

        if (data?.request?.phone) {
          setPhone(data.request.phone);
        }
      } catch (err) {
        if (cancelled) return;

        setValid(false);

        setError(
          formatApiErrorDetail(
            err.response?.data?.detail,
          ) ||
            "Não foi possível validar este link de primeiro acesso.",
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    validate();

    return () => {
      cancelled = true;
    };
  }, [token]);


  /* ==============================================================
     ÁREAS
  ============================================================== */

  useEffect(() => {
    let cancelled = false;

    async function loadAreas() {
      if (
        !valid ||
        !isAreaResponsible ||
        !token
      ) {
        setAreas([]);
        setSelectedAreaIds([]);
        return;
      }

      setAreasLoading(true);
      setError("");

      try {
        const { data } = await axios.get(
          `${API}/auth/registration/areas`,
          {
            params: {
              token,
            },
          },
        );

        if (cancelled) return;

        setAreas(
          Array.isArray(data?.areas)
            ? data.areas
            : [],
        );
      } catch (err) {
        if (cancelled) return;

        setAreas([]);

        setError(
          formatApiErrorDetail(
            err.response?.data?.detail,
          ) ||
            "Não foi possível carregar as áreas da unidade.",
        );
      } finally {
        if (!cancelled) {
          setAreasLoading(false);
        }
      }
    }

    loadAreas();

    return () => {
      cancelled = true;
    };
  }, [
    valid,
    isAreaResponsible,
    token,
  ]);


  /* ==============================================================
     SENHA
  ============================================================== */

  const passwordInfo = useMemo(
    () => passwordStrength(password),
    [password],
  );


  const passwordsMatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    password === confirmPassword;


  const passwordMismatch =
    confirmPassword.length > 0 &&
    password !== confirmPassword;


  /* ==============================================================
     ÁREAS
  ============================================================== */

  const toggleArea = (areaId) => {
    const id = String(areaId);

    setSelectedAreaIds((current) => {
      if (current.includes(id)) {
        return current.filter(
          (item) => item !== id,
        );
      }

      return [
        ...current,
        id,
      ];
    });
  };


  const selectAllAreas = () => {
    setSelectedAreaIds(
      areas.map((area) =>
        String(area.id),
      ),
    );
  };


  const clearAreas = () => {
    setSelectedAreaIds([]);
  };


  /* ==============================================================
     SUBMIT
  ============================================================== */

  const submit = async (event) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    if (!valid) {
      setError(
        "Este link de primeiro acesso não está mais disponível.",
      );
      return;
    }

    if (!password) {
      setError(
        "Informe uma senha.",
      );
      return;
    }

    if (password.length < 6) {
      setError(
        "A senha deve possuir pelo menos 6 caracteres.",
      );
      return;
    }

    if (!confirmPassword) {
      setError(
        "Confirme sua senha.",
      );
      return;
    }

    if (password !== confirmPassword) {
      setError(
        "As senhas não conferem.",
      );
      return;
    }

    if (
      isAreaResponsible &&
      selectedAreaIds.length === 0
    ) {
      setError(
        "Selecione pelo menos uma área sob sua responsabilidade.",
      );
      return;
    }

    setSubmitting(true);

    try {
      const { data } = await axios.post(
        `${API}/auth/registration/complete`,
        {
          password,
          confirm_password:
            confirmPassword,

          phone:
            phone.trim(),

          is_area_responsible:
            isAreaResponsible,

          responsible_area_ids:
            isAreaResponsible
              ? selectedAreaIds
              : [],
        },
        {
          params: {
            token,
          },
        },
      );

      setSuccess(
        data?.message ||
          "Cadastro finalizado com sucesso.",
      );

      /*
       * O token não deve permanecer disponível
       * no estado visual depois da conclusão.
       */

      setValid(false);

    } catch (err) {
      setError(
        formatApiErrorDetail(
          err.response?.data?.detail,
        ) ||
          "Não foi possível finalizar o cadastro.",
      );
    } finally {
      setSubmitting(false);
    }
  };


  /* ==============================================================
     LOADING
  ============================================================== */

  if (loading) {
    return (
      <PageShell branding={branding}>
        <Card className="w-full max-w-xl">
          <CardContent className="py-16">
            <div className="flex flex-col items-center justify-center text-center gap-4">
              <div className="h-12 w-12 rounded-full border-2 border-primary border-t-transparent animate-spin" />

              <div>
                <p className="font-semibold">
                  Validando seu acesso
                </p>

                <p className="text-sm text-muted-foreground mt-1">
                  Aguarde enquanto verificamos seu
                  link de primeiro acesso.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </PageShell>
    );
  }


  /* ==============================================================
     SUCESSO
  ============================================================== */

  if (success) {
    return (
      <PageShell branding={branding}>
        <Card className="w-full max-w-xl">
          <CardContent className="py-12">
            <div className="flex flex-col items-center text-center">
              <div className="h-16 w-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-5">
                <CheckCircle2
                  size={34}
                  className="text-emerald-500"
                />
              </div>

              <h1 className="font-heading text-2xl font-bold">
                Cadastro finalizado
              </h1>

              <p className="text-muted-foreground mt-3 max-w-md">
                {success}
              </p>

              <div className="rounded-lg border border-border bg-card p-4 mt-6 w-full text-left">
                <p className="text-sm font-semibold">
                  Próximo passo
                </p>

                <p className="text-sm text-muted-foreground mt-1">
                  Sua conta foi criada. Agora você
                  poderá acessar a plataforma com
                  seu e-mail e a senha definida neste
                  cadastro.
                </p>
              </div>

              <Button
                className="w-full mt-6"
                onClick={() =>
                  navigate("/login")
                }
              >
                Ir para o login
              </Button>
            </div>
          </CardContent>
        </Card>
      </PageShell>
    );
  }


  /* ==============================================================
     TOKEN INVÁLIDO
  ============================================================== */

  if (!valid) {
    return (
      <PageShell branding={branding}>
        <Card className="w-full max-w-xl">
          <CardContent className="py-12">
            <div className="flex flex-col items-center text-center">
              <div className="h-16 w-16 rounded-full bg-red-500/10 flex items-center justify-center mb-5">
                <CircleAlert
                  size={34}
                  className="text-red-400"
                />
              </div>

              <h1 className="font-heading text-2xl font-bold">
                Link indisponível
              </h1>

              <p className="text-muted-foreground mt-3 max-w-md">
                {error ||
                  "Este link de primeiro acesso é inválido, expirou ou já foi utilizado."}
              </p>

              <div className="rounded-lg border border-border bg-card p-4 mt-6 w-full text-left">
                <p className="text-sm font-semibold">
                  O que fazer?
                </p>

                <p className="text-sm text-muted-foreground mt-1">
                  Entre em contato com o administrador
                  responsável pelo seu acesso para
                  solicitar um novo link de finalização.
                </p>
              </div>

              <Button
                variant="outline"
                className="w-full mt-6"
                onClick={() =>
                  navigate("/login")
                }
              >
                Voltar para o login
              </Button>
            </div>
          </CardContent>
        </Card>
      </PageShell>
    );
  }


  /* ==============================================================
     CADASTRO
  ============================================================== */

  return (
    <PageShell branding={branding}>
      <div className="w-full max-w-3xl space-y-5">

        {/* ========================================================
            CABEÇALHO
        ======================================================== */}

        <div className="text-center">
          <div className="inline-flex h-14 w-14 rounded-2xl bg-primary/10 items-center justify-center mb-4">
            <ShieldCheck
              size={28}
              className="text-primary"
            />
          </div>

          <h1 className="font-heading text-2xl sm:text-3xl font-bold">
            Finalize seu cadastro
          </h1>

          <p className="text-sm text-muted-foreground mt-2 max-w-xl mx-auto">
            Seu acesso foi aprovado. Complete os
            dados abaixo para concluir a criação
            da sua conta na plataforma ASTRAL.
          </p>
        </div>


        {/* ========================================================
            PRÉ-CADASTRO
        ======================================================== */}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User size={18} />
              Dados do pré-cadastro
            </CardTitle>

            <CardDescription>
              Estas informações foram definidas na
              solicitação e aprovação do seu acesso.
            </CardDescription>
          </CardHeader>

          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <ReadOnlyField
              label="Nome"
              value={
                request?.name
              }
            />

            <ReadOnlyField
              label="E-mail"
              value={
                request?.email
              }
            />

            <ReadOnlyField
              label="Empresa"
              value={
                request?.company?.name ||
                request?.company_name
              }
            />

            <ReadOnlyField
              label="Unidade"
              value={
                request?.unit?.name ||
                request?.unit_name
              }
            />

            <ReadOnlyField
              label="Perfil aprovado"
              value={
                normalizeRole(
                  request?.requested_role,
                )
              }
            />

            <ReadOnlyField
              label="Protocolo"
              value={
                request?.protocol
              }
            />

          </CardContent>
        </Card>


        {/* ========================================================
            FORMULÁRIO
        ======================================================== */}

        <form
          onSubmit={submit}
          className="space-y-5"
        >

          {/* ======================================================
              DADOS COMPLEMENTARES
          ====================================================== */}

          <Card>
            <CardHeader>
              <CardTitle>
                Dados complementares
              </CardTitle>

              <CardDescription>
                Complete as informações necessárias
                para sua conta.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-5">

              <div className="space-y-2">
                <Label htmlFor="phone">
                  Telefone
                </Label>

                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(event) =>
                    setPhone(
                      event.target.value,
                    )
                  }
                  placeholder="(00) 00000-0000"
                  autoComplete="tel"
                />
              </div>

            </CardContent>
          </Card>


          {/* ======================================================
              SENHA
          ====================================================== */}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound size={18} />
                Defina sua senha
              </CardTitle>

              <CardDescription>
                Esta senha será utilizada para seus
                próximos acessos à plataforma.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-5">

              <div className="space-y-2">
                <Label htmlFor="password">
                  Nova senha
                </Label>

                <div className="relative">
                  <Input
                    id="password"
                    type={
                      showPassword
                        ? "text"
                        : "password"
                    }
                    value={password}
                    onChange={(event) =>
                      setPassword(
                        event.target.value,
                      )
                    }
                    placeholder="Digite sua senha"
                    autoComplete="new-password"
                    className="pr-11"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword(
                        (value) =>
                          !value,
                      )
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={
                      showPassword
                        ? "Ocultar senha"
                        : "Mostrar senha"
                    }
                  >
                    {showPassword ? (
                      <EyeOff size={17} />
                    ) : (
                      <Eye size={17} />
                    )}
                  </button>
                </div>

                {passwordInfo.label && (
                  <div className="space-y-1.5">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map(
                        (item) => (
                          <div
                            key={item}
                            className={[
                              "h-1.5 flex-1 rounded-full",
                              item <=
                              passwordInfo.score
                                ? "bg-primary"
                                : "bg-muted",
                            ].join(" ")}
                          />
                        ),
                      )}
                    </div>

                    <p className="text-xs text-muted-foreground">
                      Força da senha:{" "}
                      <span className="font-medium text-foreground">
                        {passwordInfo.label}
                      </span>
                    </p>
                  </div>
                )}
              </div>


              <div className="space-y-2">
                <Label htmlFor="confirm-password">
                  Confirmar senha
                </Label>

                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={
                      showConfirmPassword
                        ? "text"
                        : "password"
                    }
                    value={
                      confirmPassword
                    }
                    onChange={(event) =>
                      setConfirmPassword(
                        event.target.value,
                      )
                    }
                    placeholder="Digite a senha novamente"
                    autoComplete="new-password"
                    className="pr-11"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowConfirmPassword(
                        (value) =>
                          !value,
                      )
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={
                      showConfirmPassword
                        ? "Ocultar confirmação"
                        : "Mostrar confirmação"
                    }
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={17} />
                    ) : (
                      <Eye size={17} />
                    )}
                  </button>
                </div>

                {passwordMismatch && (
                  <p className="text-xs text-red-400">
                    As senhas não conferem.
                  </p>
                )}

                {passwordsMatch && (
                  <p className="text-xs text-emerald-500 flex items-center gap-1">
                    <CheckCircle2 size={13} />
                    Senhas conferem.
                  </p>
                )}
              </div>

            </CardContent>
          </Card>


          {/* ======================================================
              RESPONSÁVEL DE ÁREA
          ====================================================== */}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users size={18} />
                Responsabilidade de área
              </CardTitle>

              <CardDescription>
                Informe se você será responsável por
                uma ou mais áreas da unidade.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-5">

              <div
                className={[
                  "rounded-xl border p-4 transition-colors",
                  isAreaResponsible
                    ? "border-primary/40 bg-primary/5"
                    : "border-border",
                ].join(" ")}
              >

                <div className="flex items-start gap-3">

                  <Checkbox
                    id="is-area-responsible"
                    checked={
                      isAreaResponsible
                    }
                    onCheckedChange={(
                      checked,
                    ) => {
                      const value =
                        checked === true;

                      setIsAreaResponsible(
                        value,
                      );

                      if (!value) {
                        setSelectedAreaIds(
                          [],
                        );
                      }
                    }}
                  />

                  <div className="space-y-1">
                    <Label
                      htmlFor="is-area-responsible"
                      className="cursor-pointer font-medium"
                    >
                      Sou responsável de área
                    </Label>

                    <p className="text-xs text-muted-foreground">
                      Selecione esta opção caso
                      você seja responsável por
                      uma ou mais áreas da unidade.
                    </p>
                  </div>

                </div>

              </div>


              {isAreaResponsible && (
                <div className="space-y-4">

                  <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                    <div className="flex items-start gap-3">
                      <MapPin
                        size={18}
                        className="text-primary mt-0.5 shrink-0"
                      />

                      <div>
                        <p className="text-sm font-medium">
                          Áreas da unidade
                        </p>

                        <p className="text-xs text-muted-foreground mt-1">
                          Você pode selecionar
                          <b> várias áreas</b>.
                          Esses vínculos serão
                          cadastrados automaticamente
                          como suas áreas de
                          responsabilidade.
                        </p>
                      </div>
                    </div>
                  </div>


                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium">
                      Selecione as áreas
                    </p>

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={
                          selectAllAreas
                        }
                        disabled={
                          areasLoading ||
                          areas.length === 0
                        }
                      >
                        Selecionar todas
                      </Button>

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={
                          clearAreas
                        }
                        disabled={
                          selectedAreaIds.length === 0
                        }
                      >
                        Limpar
                      </Button>
                    </div>
                  </div>


                  {areasLoading ? (
                    <div className="rounded-lg border border-border py-10 flex flex-col items-center justify-center text-center">
                      <Loader2
                        size={22}
                        className="animate-spin text-primary"
                      />

                      <p className="text-sm text-muted-foreground mt-2">
                        Carregando áreas...
                      </p>
                    </div>
                  ) : areas.length === 0 ? (
                    <div className="rounded-lg border border-border bg-muted/20 py-10 px-5 text-center">
                      <CircleAlert
                        size={24}
                        className="mx-auto text-muted-foreground"
                      />

                      <p className="text-sm font-medium mt-3">
                        Nenhuma área disponível
                      </p>

                      <p className="text-xs text-muted-foreground mt-1">
                        A unidade vinculada ao
                        seu pré-cadastro não possui
                        áreas disponíveis para seleção.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {areas.map(
                        (area) => {
                          const id =
                            String(
                              area.id,
                            );

                          const checked =
                            selectedAreaIds.includes(
                              id,
                            );

                          return (
                            <button
                              key={id}
                              type="button"
                              onClick={() =>
                                toggleArea(
                                  id,
                                )
                              }
                              className={[
                                "w-full text-left rounded-lg border p-3 transition-all",
                                checked
                                  ? "border-primary bg-primary/10"
                                  : "border-border hover:border-primary/40 hover:bg-muted/30",
                              ].join(" ")}
                            >
                              <div className="flex items-center gap-3">

                                <Checkbox
                                  checked={
                                    checked
                                  }
                                  onCheckedChange={() =>
                                    toggleArea(
                                      id,
                                    )
                                  }
                                  onClick={(
                                    event,
                                  ) =>
                                    event.stopPropagation()
                                  }
                                />

                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate">
                                    {area.name ||
                                      "Área sem nome"}
                                  </p>

                                  <p className="text-[11px] text-muted-foreground">
                                    Unidade vinculada
                                  </p>
                                </div>

                              </div>
                            </button>
                          );
                        },
                      )}
                    </div>
                  )}


                  {selectedAreaIds.length >
                    0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedAreaIds.map(
                        (id) => {
                          const area =
                            areas.find(
                              (item) =>
                                String(
                                  item.id,
                                ) === id,
                            );

                          return (
                            <Badge
                              key={id}
                              variant="secondary"
                            >
                              {area?.name ||
                                id}
                            </Badge>
                          );
                        },
                      )}
                    </div>
                  )}

                </div>
              )}

            </CardContent>
          </Card>


          {/* ======================================================
              ERRO
          ====================================================== */}

          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3">
              <div className="flex items-start gap-3">
                <CircleAlert
                  size={18}
                  className="text-red-400 mt-0.5 shrink-0"
                />

                <p className="text-sm text-red-300">
                  {error}
                </p>
              </div>
            </div>
          )}


          {/* ======================================================
              FINALIZAR
          ====================================================== */}

          <Button
            type="submit"
            className="w-full h-11"
            disabled={
              submitting ||
              !valid
            }
          >
            {submitting ? (
              <>
                <Loader2
                  size={17}
                  className="mr-2 animate-spin"
                />

                Finalizando cadastro...
              </>
            ) : (
              <>
                <CheckCircle2
                  size={17}
                  className="mr-2"
                />

                Finalizar cadastro
              </>
            )}
          </Button>


          <p className="text-center text-xs text-muted-foreground">
            Ao finalizar, sua conta será criada
            com as informações aprovadas pelo
            administrador e os dados complementares
            informados nesta etapa.
          </p>

        </form>

      </div>
    </PageShell>
  );
}


/* ================================================================
   READ ONLY FIELD
================================================================ */

function ReadOnlyField({
  label,
  value,
}) {
  return (
    <div className="space-y-1.5">
      <Label>
        {label}
      </Label>

      <div className="min-h-10 rounded-md border border-border bg-muted/30 px-3 py-2 flex items-center">
        <span className="text-sm truncate">
          {value || "Não informado"}
        </span>
      </div>
    </div>
  );
}


/* ================================================================
   PAGE SHELL
================================================================ */

function PageShell({
  children,
  branding,
}) {
  const companyName =
    branding?.company_name ||
    "ASTRAL Saúde Ambiental";

  const systemName =
    branding?.system_name ||
    "Control Intelligence";

  const logo =
    branding?.logo_main ||
    branding?.logo ||
    "";

  return (
    <div className="min-h-screen bg-background flex flex-col">

      <header className="border-b border-border bg-card/80 backdrop-blur">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between gap-4">

          <div className="flex items-center gap-3 min-w-0">

            {logo ? (
              <img
                src={logo}
                alt={companyName}
                className="h-10 w-10 rounded-lg object-cover shrink-0"
              />
            ) : (
              <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center shrink-0">
                <ShieldCheck
                  size={21}
                  className="text-white"
                />
              </div>
            )}

            <div className="min-w-0">
              <p className="font-heading font-bold leading-none truncate">
                {companyName}
              </p>

              <p className="text-[10px] text-muted-foreground tracking-[0.18em] uppercase mt-1 truncate">
                {systemName}
              </p>
            </div>

          </div>

          <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck size={14} />
            Acesso seguro
          </div>

        </div>
      </header>


      <main className="flex-1 px-5 py-8 sm:py-10 flex justify-center">
        {children}
      </main>


      <footer className="border-t border-border">
        <div className="max-w-5xl mx-auto px-5 py-4 text-center text-xs text-muted-foreground">
          © 2026 {companyName}
        </div>
      </footer>

    </div>
  );
}